import React from 'react';
import sanitizeHtml from '../../utils/sanitizeHtml';
import annotateConditions from '../../utils/annotateConditions';
import { useConditionTerms } from '../../context/ConditionTermsContext';
import ConditionCardLayer from './ConditionCardLayer';

/**
 * The one place authored HTML becomes DOM.
 *
 * `DetailSection` had three separate `dangerouslySetInnerHTML` call sites, each
 * with its own `eslint-disable` next to it, and each was an independent
 * opportunity to forget the sanitiser — which is how all three came to be
 * unsanitised at once. Routing them through a single component means a fourth
 * prose style added later inherits the fix instead of re-introducing the bug,
 * and it gives the guard something to assert against by name.
 *
 * Renders a `<div>` because every caller wrapped it in one; `className` carries
 * the caller's `prose-content` (Tailwind Typography) styling through unchanged.
 *
 * CONDITION TERMS (issue 36, phase 4b). When the entry on screen declares
 * conditions, the ones it names in its own prose are linked and given a hover
 * card. The annotation runs on the SANITISED string, never on authored HTML,
 * and inserts only anchors it builds itself. With no conditions in context this
 * is byte-for-byte the component it was before.
 */
export default function RichText({ html, className }) {
  const conditions = useConditionTerms();
  const sanitized = sanitizeHtml(html);

  if (!conditions.length) {
    // eslint-disable-next-line react/no-danger
    return <div className={className} dangerouslySetInnerHTML={{ __html: sanitized }} />;
  }

  return (
    <ConditionCardLayer conditions={conditions}>
      {/* eslint-disable-next-line react/no-danger */}
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: annotateConditions(sanitized, conditions) }}
      />
    </ConditionCardLayer>
  );
}
